import React, { useEffect, useMemo, useState } from 'react';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  TextField,
} from '@material-ui/core';
import TimePicker from 'react-time-picker';
import { AddCircleOutlineOutlined } from '@material-ui/icons';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import Tooltip from '@material-ui/core/Tooltip';
import './availabilitySlots.css';
import 'react-time-picker/dist/TimePicker.css';
import 'react-clock/dist/Clock.css';
import { AppButton } from '../../packages/AppButton';
import CalendlyConnectButton from '../Calendly/CalendlyConnectButton';

const pageConfig = {
  appointment_booking: {
    header: 'Schedule+ Client Appointment Preferences',
    clearLabel: 'Deactivate Schedule+ for Clients',
    subHeader:
      'NOTE: Pick days and times which you would like our chat agents to book appointments for you. Once you recieve a booking notification from AidQuest, You have to contact the client to confirm and/or adjust the time.',
  },
  interview_booking: {
    header: 'Schedule+ Employee Interview Preferences',
    clearLabel: 'Deactivate Schedule+ for Employees',
    subHeader:
      'NOTE: Pick days and times which you would like our chat agents to book appointments for you. Once you recieve a booking notification from AidQuest, You have to contact the applicant to confirm and/or adjust the time.',
  },
};

const possibleTerms = {
  caregiverInterviewTerm: ['Office Interview', 'Phone Interview'],
  clientAppointmentTerm: [
    'Free In Home Assessment (Ask for Address)',
    'Complimentary Evaluation',
    'Phone Evaluation',
  ],
};

export default function AvailabilitySlotPreferences(props) {
  const { apiFormData, pageType, handleChange, calendlyOAuth } = props;
  const [slots, setSlots] = useState([
    {
      dayOfWeek: 'MONDAY',
      slotsAvailable: false,
      timeframes: [],
    },
    {
      dayOfWeek: 'TUESDAY',
      slotsAvailable: false,
      timeframes: [],
    },
    {
      dayOfWeek: 'WEDNESDAY',
      slotsAvailable: false,
      timeframes: [],
    },
    {
      dayOfWeek: 'THURSDAY',
      slotsAvailable: false,
      timeframes: [],
    },
    {
      dayOfWeek: 'FRIDAY',
      slotsAvailable: false,
      timeframes: [],
    },
    {
      dayOfWeek: 'SATURDAY',
      slotsAvailable: false,
      timeframes: [],
    },
    {
      dayOfWeek: 'SUNDAY',
      slotsAvailable: false,
      timeframes: [],
    },
  ]);
  const [radioData, setRadioData] = useState({});
  const [isCalendlyUrlValid, setIsCalendlyUrlValid] = useState(false);

  useEffect(() => {
    if (apiFormData) {
      handelIncomingApiData(apiFormData);
    }
  }, [props?.apiFormData]);

  function handelIncomingApiData(apiRes) {
    // Setting Time Frames
    const apiData = { ...apiRes };
    const slotsData = [...slots];
    apiData.timeFrames.forEach((obj, index) => {
      slotsData[index] = { ...slotsData[index], ...obj };
    });
    setSlots([...slotsData]);

    // Setting Radio Data
    const termKey =
      pageType === 'interview_booking'
        ? 'caregiverInterviewTerm'
        : 'clientAppointmentTerm';
    const calendlyUrlKey =
      pageType === 'interview_booking'
        ? 'calendlyCaregiverInterviewUrl'
        : 'calendlyClientAppointmentUrl';

    let tempRadioData = {
      ...apiData?.radioAndFieldData,
    };

    if (tempRadioData[calendlyUrlKey]) {
      setIsCalendlyUrlValid(isUrlValid(tempRadioData[calendlyUrlKey]));
    }

    if (
      (tempRadioData?.[termKey] || tempRadioData?.[termKey] === '') &&
      !possibleTerms[termKey]?.includes(tempRadioData?.[termKey])
    ) {
      tempRadioData = {
        ...tempRadioData,
        [termKey]: 'Other',
        otherTerm: tempRadioData?.[termKey],
      };
    }

    setRadioData({ ...tempRadioData });
    onSubmit(slots, tempRadioData, false);
  }

  const handelRadioDataChange = (key, event) => {
    const val = event?.target?.value;
    const dataToSend = { ...radioData, [key]: val };

    const calendlyUrlKey =
      pageType === 'interview_booking'
        ? 'calendlyCaregiverInterviewUrl'
        : 'calendlyClientAppointmentUrl';

    const calendarTypeKey =
      pageType === 'interview_booking'
        ? 'caregiverSchedulingCalender'
        : 'clientSchedulingCalender';

    if (key === calendlyUrlKey) {
      setIsCalendlyUrlValid(isUrlValid(val));
    }

    if (key === calendarTypeKey) {
      if (val === 'CALENDLY') {
        const tempSlots = slots?.map((ele) => ({
          ...ele,
          slotsAvailable: false,
          timeframes: [],
        }));
        setSlots(tempSlots);
      } else {
        dataToSend[calendlyUrlKey] = '';
      }
    }
    setRadioData({ ...dataToSend });
    onSubmit(slots, dataToSend);
  };

  const onSubmit = (timeArr, rdoData, sendData = true) => {
    const dataArr = timeArr;
    const tempRadioData = JSON.parse(JSON.stringify(rdoData)) || {};
    const errAndAvailableStatus = checkFormValidOrNot(dataArr, tempRadioData);
    const error = errAndAvailableStatus?.error;
    if (
      tempRadioData?.caregiverInterviewTerm &&
      tempRadioData?.caregiverInterviewTerm === 'Other'
    ) {
      tempRadioData.caregiverInterviewTerm = tempRadioData?.otherTerm || '';
    }
    if (
      tempRadioData?.clientAppointmentTerm &&
      tempRadioData?.clientAppointmentTerm === 'Other'
    ) {
      tempRadioData.clientAppointmentTerm = tempRadioData?.otherTerm || '';
    }
    delete tempRadioData?.otherTerm;
    const timeSlotsKey =
      pageType === 'interview_booking'
        ? 'employeeInterviewBookingPreferences'
        : 'clientBookingPreferences';
    handleChange(
      sendData ? { [timeSlotsKey]: dataArr, ...tempRadioData } : null,
      error,
      pageType,
      errAndAvailableStatus?.dataAvailable,
    );
  };

  const checkFormValidOrNot = (data, rdoData) => {
    let error = false;
    const termKey =
      pageType === 'interview_booking'
        ? 'caregiverInterviewTerm'
        : 'clientAppointmentTerm';
    const calendlyUrlKey =
      pageType === 'interview_booking'
        ? 'calendlyCaregiverInterviewUrl'
        : 'calendlyClientAppointmentUrl';
    const calendarTypeKey =
      pageType === 'interview_booking'
        ? 'caregiverSchedulingCalender'
        : 'clientSchedulingCalender';
    const formRequiredKeys = [termKey, calendarTypeKey];
    if (rdoData[calendarTypeKey] === 'CALENDLY') {
      formRequiredKeys.push(calendlyUrlKey);
    }

    // Err if no slots
    if (rdoData[calendarTypeKey] === 'AIDQUEST_INTERNAL') {
      let totalSlots = 0;
      data.forEach((ele) => {
        totalSlots += ele?.timeframes?.length || 0;
      });

      if (!totalSlots > 0) {
        error = true;
      }
    }

    // Check Error in timeframes
    data.forEach((ele) => {
      ele.timeframes.forEach((daySlot) => {
        if (!daySlot.to || !daySlot.from) {
          error = true;
        }
      });
    });

    // Check term Err
    if (rdoData?.[termKey] === 'Other' && !rdoData?.otherTerm) {
      error = true;
    }

    // check calendly url err
    if (
      (rdoData?.[calendarTypeKey] === 'CALENDLY' &&
        !rdoData?.[calendlyUrlKey]) ||
      (rdoData?.[calendlyUrlKey] && !isUrlValid(rdoData?.[calendlyUrlKey]))
    ) {
      error = true;
    }

    formRequiredKeys.forEach((key) => {
      if (!rdoData[key]) {
        error = true;
      }
    });

    // check is all not avalilable
    let isAnyRequiredKeysAvailable = false;
    let totalSlots = 0;
    formRequiredKeys.forEach((key) => {
      if (rdoData[key]) {
        isAnyRequiredKeysAvailable = true;
      }
    });
    data.forEach((ele) => {
      totalSlots += ele?.timeframes?.length || 0;
    });

    if (totalSlots > 0) {
      isAnyRequiredKeysAvailable = true;
    }

    if (!isAnyRequiredKeysAvailable) {
      error = false;
    }

    return { error, dataAvailable: isAnyRequiredKeysAvailable };
  };

  const isUrlValid = (value) => {
    const urlRegex = /\bhttps?:\/\/[^\s/$.?#].[^\s]*\b/g;
    return value.match(urlRegex) || false;
  };

  const addNewTimeRange = (slotsArrayIndex) => {
    const currentSlots = [...slots];
    if (currentSlots[slotsArrayIndex].slotsAvailable === false) {
      currentSlots[slotsArrayIndex].slotsAvailable = true;
    }
    currentSlots[slotsArrayIndex].timeframes.push({
      from: '',
      to: '',
    });
    setSlots(currentSlots);
    onSubmit(currentSlots, radioData);
  };

  const deleteTimeRange = (slotsArrayIndex) => {
    const slotsArr = [...slots];
    slotsArr[slotsArrayIndex].timeframes.pop();
    if (slotsArr[slotsArrayIndex].timeframes.length === 0) {
      slotsArr[slotsArrayIndex].slotsAvailable = false;
    }
    setSlots(slotsArr);
    onSubmit(slotsArr, radioData);
  };

  const copyTimeRange = (slotsArrayIndex) => {
    const slotsArr = JSON.parse(JSON.stringify(slots));
    slotsArr[slotsArrayIndex + 1].timeframes =
      slots[slotsArrayIndex].timeframes;

    slotsArr[slotsArrayIndex + 1].slotsAvailable =
      slots[slotsArrayIndex].slotsAvailable;

    setSlots(slotsArr);
    onSubmit(slotsArr, radioData);
  };

  const onCheckboxToggle = (event, slotsArrayIndex) => {
    const checkboxVal = event.target.checked;
    const currentSlots = [...slots];
    if (checkboxVal === true) {
      currentSlots[slotsArrayIndex].timeframes = [
        {
          from: '',
          to: '',
        },
      ];
    } else {
      currentSlots[slotsArrayIndex].timeframes = [];
    }
    currentSlots[slotsArrayIndex].slotsAvailable = checkboxVal;
    setSlots(currentSlots);
    onSubmit(currentSlots, radioData);
  };

  const onTimeValueChange = (event, slotsArrayIndex, timeFrameIndex, key) => {
    const slotsArray = [...slots];
    const fieldVal = event;
    slotsArray[slotsArrayIndex].timeframes[timeFrameIndex][key] = fieldVal;
    setSlots(slotsArray);
    onSubmit(slotsArray, radioData);
  };

  const clearForm = () => {
    const termKey =
      pageType === 'interview_booking'
        ? 'caregiverInterviewTerm'
        : 'clientAppointmentTerm';
    const calendarTypeKey =
      pageType === 'interview_booking'
        ? 'caregiverSchedulingCalender'
        : 'clientSchedulingCalender';
    const calendlyUrlKey =
      pageType === 'interview_booking'
        ? 'calendlyCaregiverInterviewUrl'
        : 'calendlyClientAppointmentUrl';
    const timeSlotsKey =
      pageType === 'interview_booking'
        ? 'employeeInterviewBookingPreferences'
        : 'clientBookingPreferences';

    const tempRadioData = {
      [termKey]: null,
      [calendarTypeKey]: null,
      [calendlyUrlKey]: null,
    };

    const tempSlots = [
      {
        dayOfWeek: 'MONDAY',
        slotsAvailable: false,
        timeframes: [],
      },
      {
        dayOfWeek: 'TUESDAY',
        slotsAvailable: false,
        timeframes: [],
      },
      {
        dayOfWeek: 'WEDNESDAY',
        slotsAvailable: false,
        timeframes: [],
      },
      {
        dayOfWeek: 'THURSDAY',
        slotsAvailable: false,
        timeframes: [],
      },
      {
        dayOfWeek: 'FRIDAY',
        slotsAvailable: false,
        timeframes: [],
      },
      {
        dayOfWeek: 'SATURDAY',
        slotsAvailable: false,
        timeframes: [],
      },
      {
        dayOfWeek: 'SUNDAY',
        slotsAvailable: false,
        timeframes: [],
      },
    ];

    setRadioData({});
    setSlots(tempSlots);
    handleChange(
      { [timeSlotsKey]: tempSlots, ...tempRadioData },
      false,
      pageType,
      false,
    );
  };

  return useMemo(
    () => (
      <>
        <div className="parent-container">
          <div className="header-container-with-action">
            <h2 className="header">{pageConfig[pageType].header}</h2>
            <AppButton
              size="small"
              label={pageConfig[pageType].clearLabel}
              variant="contained"
              handleClick={() => {
                clearForm();
              }}
            />
          </div>

          {pageType === 'appointment_booking' && (
            <div style={{ paddingLeft: '15px', marginTop: '15px' }}>
              <FormControl>
                <h3 className="radio-label">
                  What would you like our agents to call the client appointment?
                  <span style={{ marginLeft: '5px', color: 'red' }}>*</span>
                </h3>
                <RadioGroup
                  name="clientAppointmentTerm"
                  value={radioData.clientAppointmentTerm || ''}
                  onChange={(event) => {
                    handelRadioDataChange('clientAppointmentTerm', event);
                  }}
                >
                  <FormControlLabel
                    classes={{ label: 'form-control-label' }}
                    value="Free In Home Assessment (Ask for Address)"
                    control={<Radio />}
                    className="control-label"
                    label="Free In Home Assessment (Ask for Address)"
                  />
                  <FormControlLabel
                    classes={{ label: 'form-control-label' }}
                    className="control-label"
                    value="Complimentary Evaluation"
                    control={<Radio />}
                    label="Complimentary Evaluation"
                  />
                  <FormControlLabel
                    classes={{ label: 'form-control-label' }}
                    className="control-label"
                    value="Phone Evaluation"
                    control={<Radio />}
                    label="Phone Evaluation"
                  />
                  <div className="input-span" style={{}}>
                    <FormControlLabel
                      classes={{ label: 'form-control-label' }}
                      value="Other"
                      className="control-label"
                      control={<Radio />}
                      label="Other"
                    />
                    {radioData.clientAppointmentTerm === 'Other' && (
                      <TextField
                        size="small"
                        error={
                          radioData.clientAppointmentTerm === 'Other' &&
                          !radioData.otherTerm
                        }
                        value={radioData.otherTerm}
                        onChange={(event) => {
                          handelRadioDataChange('otherTerm', event);
                        }}
                        label="Add your own term"
                        variant="outlined"
                      />
                    )}
                  </div>
                </RadioGroup>
              </FormControl>

              <br></br>
              <br></br>

              <FormControl>
                <h3 className="radio-label">
                  What calendar would you like our agents to use to set-up
                  appointments for clients?
                  <span style={{ marginLeft: '5px', color: 'red' }}>*</span>
                </h3>
                <RadioGroup
                  name="clientSchedulingCalender"
                  value={radioData.clientSchedulingCalender || ''}
                  onChange={(event) => {
                    handelRadioDataChange('clientSchedulingCalender', event);
                  }}
                >
                  <FormControlLabel
                    classes={{ label: 'form-control-label' }}
                    value="AIDQUEST_INTERNAL"
                    control={<Radio />}
                    className="control-label"
                    label="Internal AidQuest calendar (See below)."
                  />
                  <FormControlLabel
                    classes={{ label: 'form-control-label' }}
                    className="control-label"
                    value="CALENDLY"
                    control={<Radio />}
                    label="Your Calendly Link. Please make sure your Calendly is linked to your office calendar. For more information contact support@aidquest.com."
                  />
                </RadioGroup>
              </FormControl>
            </div>
          )}

          {pageType === 'interview_booking' && (
            <div style={{ paddingLeft: '15px', marginTop: '15px' }}>
              <FormControl>
                <h3 className="radio-label">
                  What would you like our agents to call the caregiver
                  interview?
                  <span style={{ marginLeft: '5px', color: 'red' }}>*</span>
                </h3>
                <RadioGroup
                  name="caregiverInterviewTerm"
                  value={radioData.caregiverInterviewTerm || ''}
                  onChange={(event) => {
                    handelRadioDataChange('caregiverInterviewTerm', event);
                  }}
                >
                  <FormControlLabel
                    classes={{ label: 'form-control-label' }}
                    value="Office Interview"
                    control={<Radio />}
                    className="control-label"
                    label="Office Interview"
                  />
                  <FormControlLabel
                    classes={{ label: 'form-control-label' }}
                    className="control-label"
                    value="Phone Interview"
                    control={<Radio />}
                    label="Phone Interview"
                  />
                  <div className="input-span" style={{}}>
                    <FormControlLabel
                      classes={{ label: 'form-control-label' }}
                      value="Other"
                      className="control-label"
                      control={<Radio />}
                      label="Other"
                    />
                    {radioData.caregiverInterviewTerm === 'Other' && (
                      <TextField
                        size="small"
                        error={
                          radioData.caregiverInterviewTerm === 'Other' &&
                          !radioData.otherTerm
                        }
                        value={radioData.otherTerm}
                        onChange={(event) => {
                          handelRadioDataChange('otherTerm', event);
                        }}
                        label="Add your own term"
                        variant="outlined"
                      />
                    )}
                  </div>
                </RadioGroup>
              </FormControl>

              <br></br>
              <br></br>

              <FormControl>
                <h3 className="radio-label">
                  What calendar would you like our agents to use to set-up
                  interviews for caregivers?
                  <span style={{ marginLeft: '5px', color: 'red' }}>*</span>
                </h3>
                <RadioGroup
                  name="caregiverSchedulingCalender"
                  value={radioData.caregiverSchedulingCalender || ''}
                  onChange={(event) => {
                    handelRadioDataChange('caregiverSchedulingCalender', event);
                  }}
                >
                  <FormControlLabel
                    classes={{ label: 'form-control-label' }}
                    value="AIDQUEST_INTERNAL"
                    control={<Radio />}
                    className="control-label"
                    label="Internal AidQuest calendar (See below)."
                  />
                  <FormControlLabel
                    classes={{ label: 'form-control-label' }}
                    className="control-label"
                    value="CALENDLY"
                    control={<Radio />}
                    label="Your Calendly Link. Please make sure your Calendly is linked to your office calendar. For more information contact support@aidquest.com."
                  />
                </RadioGroup>
              </FormControl>
            </div>
          )}

          {((pageType === 'interview_booking' &&
            radioData?.caregiverSchedulingCalender === 'AIDQUEST_INTERNAL') ||
            (pageType === 'appointment_booking' &&
              radioData?.clientSchedulingCalender === 'AIDQUEST_INTERNAL')) && (
            <>
              <h2 className="sub-header">{pageConfig[pageType].subHeader}</h2>
              <div style={{ marginTop: '20px', overflow: 'auto' }}>
                {slots.map((obj, parentIndex) => (
                  <div className="content-container">
                    <Checkbox
                      checked={obj?.slotsAvailable}
                      onChange={(event) => {
                        onCheckboxToggle(event, parentIndex);
                      }}
                    />
                    <div>
                      <p className="day-text">{obj?.dayOfWeek}: </p>
                    </div>
                    <div className="row-slot-container">
                      {obj?.timeframes.map((childObj, childIndex) => (
                        <div className="textfield-container">
                          <div>
                            <TimePicker
                              disableClock
                              format="h:m a"
                              clearIcon={null}
                              className={
                                !slots[parentIndex].timeframes[childIndex].from
                                  ? 'time-picker-error'
                                  : 'time-picker-success'
                              }
                              onChange={(event) => {
                                onTimeValueChange(
                                  event,
                                  parentIndex,
                                  childIndex,
                                  'from',
                                );
                              }}
                              value={childObj.from}
                            />
                          </div>
                          <p style={{ marginLeft: '5px', marginRight: '5px' }}>
                            to
                          </p>
                          <div>
                            <TimePicker
                              disableClock
                              format="h:m a"
                              clearIcon={null}
                              className={
                                !slots[parentIndex].timeframes[childIndex].to
                                  ? 'time-picker-error'
                                  : 'time-picker-success'
                              }
                              onChange={(event) => {
                                onTimeValueChange(
                                  event,
                                  parentIndex,
                                  childIndex,
                                  'to',
                                );
                              }}
                              value={childObj.to}
                            />
                          </div>
                        </div>
                      ))}
                      {slots[parentIndex].timeframes.length < 3 && (
                        <Tooltip title="Add Slot">
                          <IconButton
                            onClick={() => {
                              addNewTimeRange(parentIndex);
                            }}
                            disabled={slots[parentIndex].timeframes.length >= 3}
                            style={{ color: '#033E63', padding: '0px' }}
                          >
                            <AddCircleOutlineOutlined fontSize="medium" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {slots[parentIndex].timeframes.length !== 0 &&
                        parentIndex !== slots.length - 1 && (
                          <Tooltip title="Copy same slots to next day">
                            <IconButton
                              onClick={() => {
                                copyTimeRange(parentIndex);
                              }}
                              style={{
                                color: '#033E63',
                                padding: '0px',
                              }}
                            >
                              <FileCopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      {slots[parentIndex].timeframes.length !== 0 && (
                        <Tooltip title="Remove Slot">
                          <IconButton
                            onClick={() => {
                              deleteTimeRange(parentIndex);
                            }}
                            style={{ color: '#033E63', padding: '0px' }}
                          >
                            <DeleteOutlineIcon fontSize="medium" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {((pageType === 'interview_booking' &&
            radioData?.caregiverSchedulingCalender === 'CALENDLY') ||
            (pageType === 'appointment_booking' &&
              radioData?.clientSchedulingCalender === 'CALENDLY')) && (
            <div className="calendly-text-box">
              <h2 className="calendly-text">
                Calendly URL <span style={{ color: 'red' }}>*</span>
              </h2>
              <div style={{ width: '100%' }}>
                <TextField
                  size="small"
                  style={{ width: '100%', maxWidth: '450px' }}
                  inputProps={{ 'data-testid': 'calendly-url-input' }}
                  error={
                    !radioData?.[
                      pageType === 'interview_booking'
                        ? 'calendlyCaregiverInterviewUrl'
                        : 'calendlyClientAppointmentUrl'
                    ] || !isCalendlyUrlValid
                  }
                  value={
                    radioData?.[
                      pageType === 'interview_booking'
                        ? 'calendlyCaregiverInterviewUrl'
                        : 'calendlyClientAppointmentUrl'
                    ]
                  }
                  onChange={(event) => {
                    handelRadioDataChange(
                      pageType === 'interview_booking'
                        ? 'calendlyCaregiverInterviewUrl'
                        : 'calendlyClientAppointmentUrl',
                      event,
                    );
                  }}
                  label="Calendly URL"
                  variant="outlined"
                />
                {!isCalendlyUrlValid && (
                  <p
                    style={{ marginTop: '4px', color: 'red', fontSize: '13px' }}
                  >
                    Calendly URL is not Valid!
                  </p>
                )}
                <CalendlyConnectButton
                  isConnecting={calendlyOAuth.isConnecting}
                  isConnected={calendlyOAuth.isConnected}
                  error={calendlyOAuth.error}
                  openPopup={calendlyOAuth.openPopup}
                  disconnect={calendlyOAuth.disconnect}
                />
              </div>
            </div>
          )}
        </div>
      </>
    ),
    [
      slots,
      radioData,
      isCalendlyUrlValid,
      calendlyOAuth.error,
      calendlyOAuth.isConnected,
      calendlyOAuth.isConnecting,
      calendlyOAuth.openPopup,
      calendlyOAuth.disconnect,
    ],
  );
}
